// BUILD-042 — Unified employee workspace, My HR (self). "Personal & Contacts" destination,
// composed as internal sub-tabs (Personal details / Contact details / Emergency contacts /
// Immigration) per the decision's "Single right-hand content section with useful internal
// sub-tabs." Each tab reuses an EXISTING, already-working component unchanged — this file only
// arranges them; no behavior, save path, or data source changes.
//
// The approved reference's sub-tab list for this group also shows "Family references." That
// field has no backend and was explicitly omitted from My HR per an earlier Owner direction
// ("Dependents intentionally omitted per Owner direction" — MyHrTab.tsx's prior header). Adding
// a new fourth sub-tab for it now would be new product surface, not consolidation of what
// already works — Immigration (an existing, already-built My HR section) takes that slot
// instead, preserving all four sub-tab positions from the reference without inventing a new one.
import { SubTabs } from './SubTabs';
import { PersonalDetailsSection, ImmigrationSection } from '../MyHrSections';
import { PersonalContactsPanel } from '../PersonalContactsPanel';

type Tab = 'personal' | 'contact' | 'emergency' | 'immigration';

// 2026-10-13 UAT: this composition now backs BOTH the employee self view (My HR) and the HR
// Administration employee workspace, so the two match (same four sub-tabs). `isSelf` controls
// which caller context the inner panels render for; the underlying components, data routes, and
// server authorization are unchanged — self callers hit self scope, admin callers hit company
// scope on the SAME routes, exactly as before.
export function PersonalContactsSelfSection({
  employeeId, employeeName, legalName, preferredName, isSelf = true, nameMap,
}: {
  employeeId: string; employeeName: string; legalName: string; preferredName: string | null;
  isSelf?: boolean; nameMap?: Record<string, string | null>;
}) {
  return (
    <SubTabs<Tab>
      tabs={[
        { key: 'personal', label: 'Personal details' },
        { key: 'contact', label: 'Contact details' },
        { key: 'emergency', label: 'Emergency contacts' },
        { key: 'immigration', label: 'Immigration' },
      ]}
      initial="personal"
    >
      {(active) => {
        if (active === 'personal') {
          return <PersonalDetailsSection employeeId={employeeId} employeeName={employeeName} legalName={legalName} preferredName={preferredName} forceAdmin={!isSelf} />;
        }
        if (active === 'contact') {
          return <PersonalContactsPanel employeeId={employeeId} isSelf={isSelf} nameMap={nameMap} section="contact" />;
        }
        if (active === 'emergency') {
          return <PersonalContactsPanel employeeId={employeeId} isSelf={isSelf} nameMap={nameMap} section="emergency" />;
        }
        return <ImmigrationSection />;
      }}
    </SubTabs>
  );
}
